/**
 * CompanyForm — shared between Register and Edit. The two flows share
 * every input; only the submit button label and the calling mutation
 * differ. Keeping one form prevents drift between the two screens.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Truck } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { CompanyType, CompanyOperatorMode } from '@/types/company';

export interface CompanyFormValues {
  companyType: CompanyType;
  operatorMode: CompanyOperatorMode;
  name: string;
  nameAr?: string;
  commercialRegister?: string;
  taxId?: string;
  importLicense?: string;
  country?: string;
  city?: string;
  address?: string;
  contactPhone?: string;
  contactEmail?: string;
}

interface Props {
  initial?: Partial<CompanyFormValues>;
  submitLabel: string;
  onSubmit: (values: CompanyFormValues) => Promise<void>;
  loading?: boolean;
  /** Edit-mode disables type/mode pickers — these change capabilities and
   * must go through admin reapproval. */
  lockTypeAndMode?: boolean;
}

export default function CompanyForm({ initial, submitLabel, onSubmit, loading, lockTypeAndMode }: Props) {
  const { t } = useTranslation();
  const [companyType, setCompanyType] = useState<CompanyType>(initial?.companyType || 'local');
  const [operatorMode, setOperatorMode] = useState<CompanyOperatorMode>(initial?.operatorMode || 'shipper');
  const [name, setName] = useState(initial?.name || '');
  const [nameAr, setNameAr] = useState(initial?.nameAr || '');
  const [commercialRegister, setCommercialRegister] = useState(initial?.commercialRegister || '');
  const [taxId, setTaxId] = useState(initial?.taxId || '');
  const [importLicense, setImportLicense] = useState(initial?.importLicense || '');
  const [country, setCountry] = useState(initial?.country || 'سوريا');
  const [city, setCity] = useState(initial?.city || '');
  const [address, setAddress] = useState(initial?.address || '');
  const [contactPhone, setContactPhone] = useState(initial?.contactPhone || '');
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail || '');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !contactPhone.trim()) {
      setError(t('err_company_required', 'الاسم ورقم التواصل مطلوبان'));
      return;
    }
    try {
      await onSubmit({
        companyType,
        operatorMode,
        name: name.trim(),
        nameAr: nameAr.trim() || undefined,
        commercialRegister: commercialRegister.trim() || undefined,
        taxId: taxId.trim() || undefined,
        importLicense: importLicense.trim() || undefined,
        country: country.trim() || undefined,
        city: city.trim() || undefined,
        address: address.trim() || undefined,
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim() || undefined,
      });
    } catch (e: any) {
      setError(e?.message || t('save_failed', 'فشل الحفظ'));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && <div className="bg-error/10 text-error rounded-xl p-3 text-sm">{error}</div>}

      <fieldset className="space-y-3" disabled={lockTypeAndMode}>
        <legend className="text-sm font-semibold text-text-secondary mb-2">
          {t('company_type', 'نوع الشركة')}
          {lockTypeAndMode && (
            <span className="ms-2 text-xs text-text-tertiary font-normal">
              ({t('locked_after_approval', 'مقفول بعد الاعتماد')})
            </span>
          )}
        </legend>
        <div className="grid grid-cols-2 gap-2">
          <Card
            active={companyType === 'local'}
            onClick={() => setCompanyType('local')}
            icon={<Truck size={20} />}
            title={t('company_local', 'محلية')}
            subtitle={t('company_local_subtitle', 'نقل داخل سوريا')}
            disabled={lockTypeAndMode}
          />
          <Card
            active={companyType === 'international'}
            onClick={() => setCompanyType('international')}
            icon={<Building2 size={20} />}
            title={t('company_international', 'دولية')}
            subtitle={t('company_international_subtitle', 'استيراد/حاويات')}
            disabled={lockTypeAndMode}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-2" disabled={lockTypeAndMode}>
        <legend className="text-sm font-semibold text-text-secondary mb-2">
          {t('operator_mode', 'وضع التشغيل')}
        </legend>
        <div className="flex flex-wrap gap-2">
          {(['shipper', 'operator', 'both'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setOperatorMode(m)}
              disabled={lockTypeAndMode}
              className={`px-4 py-2 rounded-xl border text-sm transition disabled:opacity-50 disabled:cursor-not-allowed ${
                operatorMode === m
                  ? 'bg-primary text-white border-primary'
                  : 'bg-surface text-text-secondary border-border hover:border-primary'
              }`}
            >
              {t(`operator_mode.${m}`, m === 'shipper' ? 'شاحن' : m === 'operator' ? 'مشغّل' : 'كلاهما')}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-text-secondary mb-1">{t('identity', 'الهوية')}</legend>
        <TextField label={t('company_name_en', 'اسم الشركة بالإنكليزية')} value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label={t('company_name_ar', 'الاسم بالعربية (اختياري)')} value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-text-secondary mb-1">{t('legal_numbers', 'الأرقام النظامية')}</legend>
        <TextField label={t('commercial_register', 'السجل التجاري')} dir="ltr" value={commercialRegister} onChange={(e) => setCommercialRegister(e.target.value)} />
        <TextField label={t('tax_id', 'الرقم الضريبي')} dir="ltr" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
        {companyType === 'international' && (
          <TextField label={t('import_license', 'رخصة الاستيراد')} dir="ltr" value={importLicense} onChange={(e) => setImportLicense(e.target.value)} />
        )}
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-text-secondary mb-1">{t('contact', 'التواصل')}</legend>
        <TextField label={t('contact_phone', 'هاتف التواصل')} type="tel" dir="ltr" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        <TextField label={t('contact_email', 'البريد الإلكتروني')} type="email" dir="ltr" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label={t('country', 'البلد')} value={country} onChange={(e) => setCountry(e.target.value)} />
          <TextField label={t('city', 'المدينة')} value={city} onChange={(e) => setCity(e.target.value)} />
        </div>
        <TextField label={t('address', 'العنوان')} value={address} onChange={(e) => setAddress(e.target.value)} />
      </fieldset>

      <Button type="submit" loading={loading} fullWidth>
        {submitLabel}
      </Button>
    </form>
  );
}

function Card({ active, onClick, icon, title, subtitle, disabled }: {
  active: boolean; onClick: () => void; icon: React.ReactNode;
  title: string; subtitle: string; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-start rounded-2xl p-4 border-2 transition disabled:opacity-60 disabled:cursor-not-allowed ${
        active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-primary/50'
      }`}
    >
      <div className={active ? 'text-primary mb-2' : 'text-text-secondary mb-2'}>{icon}</div>
      <div className="font-bold text-text-primary mb-0.5">{title}</div>
      <div className="text-xs text-text-tertiary">{subtitle}</div>
    </button>
  );
}
